import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Sparkles, ArrowLeft, X, ArrowRight, Image, FileText, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { handleEdgeFunctionError, formatErrorForToast } from "@/lib/errorHandler";

const STEPS = [
  { id: "image", title: "Foto do Produto", subtitle: "Manda aquela foto do seu produto", icon: Image },
  { id: "info", title: "Conta Pro Agente", subtitle: "Nome e detalhes pra gente criar a melhor publi", icon: FileText },
  { id: "confirm", title: "Bora Criar!", subtitle: "Revisa e dispara a IA", icon: Wand2 },
];

export default function NovaTask() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    productName: "",
    productDescription: "",
  });

  const processFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: "A imagem deve ter no máximo 10MB.", variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "Arquivo inválido", description: "Envie uma imagem (PNG, JPG ou WEBP).", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const canProceed = () => {
    if (currentStep === 0) return true; // image is optional
    if (currentStep === 1) return formData.productName.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !formData.productName.trim()) {
      toast({ title: "Campo obrigatório", description: "Por favor, informe o nome do produto.", variant: "destructive" });
      return;
    }
    if (currentStep < STEPS.length - 1) setCurrentStep(prev => prev + 1);
  };

  const handleSubmit = async () => {
    if (!formData.productName.trim()) return;
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("tasks", {
        method: "POST",
        body: {
          product_name: formData.productName.trim(),
          product_description: formData.productDescription.trim() || null,
          image_base64: imagePreview,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'task');
        if (errorInfo.action) {
          toast({
            title: errorInfo.title,
            description: errorInfo.message,
            variant: "destructive",
            action: (
              <Button variant="outline" size="sm" onClick={() => navigate(errorInfo.action!.path)} className="shrink-0">
                {errorInfo.action.label}
              </Button>
            ),
          });
        } else {
          toast(formatErrorForToast(errorInfo));
        }
        return;
      }

      // Navigate to task details to watch the generation happen
      navigate(`/dashboard/task/${data.taskId}`);
    } catch (err) {
      console.error("Submit error:", err);
      const errorInfo = handleEdgeFunctionError(err, null, 'task');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Criar Nova Publi</h1>
            <p className="text-sm text-muted-foreground">Vamos montar o influencer perfeito pro seu produto</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((step, i) => (
            <div key={step.id} className="flex items-center gap-2 flex-1">
              <button
                onClick={() => i < currentStep && setCurrentStep(i)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all w-full ${i === currentStep
                    ? "bg-primary/10 border border-primary/30 text-primary"
                    : i < currentStep
                      ? "bg-primary/5 text-primary/60 cursor-pointer hover:bg-primary/10"
                      : "bg-secondary/50 text-muted-foreground"
                  }`}
              >
                <step.icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-medium truncate hidden sm:block">{step.title}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`w-6 h-0.5 flex-shrink-0 transition-colors ${i < currentStep ? "bg-primary/30" : "bg-border"
                  }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Image */}
          {currentStep === 0 && (
            <motion.div
              key="image"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold">Foto do seu produto</h2>
                <p className="text-sm text-muted-foreground">
                  Pode ser aquela foto do iPhone mesmo — a IA se vira 📸
                </p>
              </div>

              {imagePreview ? (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="relative max-w-sm mx-auto"
                >
                  <img src={imagePreview} alt="Preview" className="w-full aspect-square object-cover rounded-2xl border border-primary/20 shadow-xl shadow-primary/5" />
                  <button
                    type="button"
                    onClick={() => setImagePreview(null)}
                    className="absolute top-3 right-3 p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors border border-border"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-3 left-3 right-3 p-2 bg-background/80 backdrop-blur-sm rounded-xl text-center">
                    <p className="text-xs font-medium text-primary">✅ Imagem carregada</p>
                  </div>
                </motion.div>
              ) : (
                <label
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center justify-center w-full aspect-[4/3] max-w-sm mx-auto border-2 border-dashed rounded-2xl cursor-pointer transition-all ${isDragging ? "border-primary bg-primary/5 scale-[1.02]" : "border-border hover:border-primary/50 hover:bg-primary/5"
                    }`}
                >
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex flex-col items-center gap-4 p-6"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-sm">Arraste ou clique aqui</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG ou WEBP (máx. 10MB)</p>
                    </div>
                  </motion.div>
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                </label>
              )}

              <p className="text-center text-xs text-muted-foreground">
                💡 A imagem é opcional, mas ajuda a IA a criar vídeos mais realistas
              </p>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1 gap-2">
                  Pular
                </Button>
                <Button onClick={handleNext} className="flex-1 gap-2" variant="hero">
                  Continuar <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Product Info */}
          {currentStep === 1 && (
            <motion.div
              key="info"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold">Fala sobre teu produto</h2>
                <p className="text-sm text-muted-foreground">
                  A IA vai criar uma persona e roteiro baseados nisso
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="productName" className="block text-sm font-medium mb-2">
                    Nome do produto <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="productName"
                    placeholder="Ex: Sérum Vitamina C, Tênis Runner Pro..."
                    value={formData.productName}
                    onChange={(e) => setFormData(prev => ({ ...prev, productName: e.target.value }))}
                    maxLength={200}
                    className="h-12 text-base rounded-xl"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="productDescription" className="block text-sm font-medium mb-2">
                    Conta mais <span className="text-muted-foreground font-normal">(opcional)</span>
                  </label>
                  <Textarea
                    id="productDescription"
                    placeholder="O que ele faz? Pra quem é? Qual o diferencial? Quanto mais detalhe, melhor o resultado..."
                    rows={4}
                    value={formData.productDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, productDescription: e.target.value }))}
                    maxLength={2000}
                    className="text-base rounded-xl resize-none"
                  />
                  <div className="flex justify-between mt-2">
                    <p className="text-xs text-muted-foreground">
                      💡 Dica: mencione público-alvo e benefícios principais
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formData.productDescription.length}/2000
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCurrentStep(0)} className="flex-1">
                  Voltar
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 gap-2"
                  variant="hero"
                  disabled={!formData.productName.trim()}
                >
                  Continuar <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirm */}
          {currentStep === 2 && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold">Tudo pronto?</h2>
                <p className="text-sm text-muted-foreground">
                  Revisa o briefing antes de soltar a IA 🤖
                </p>
              </div>

              {/* Summary card */}
              <div className="rounded-2xl border border-border/50 bg-card overflow-hidden">
                {imagePreview && (
                  <img src={imagePreview} alt="Produto" className="w-full h-40 object-cover" />
                )}
                <div className="p-5 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Produto</p>
                    <p className="font-semibold text-lg">{formData.productName}</p>
                  </div>
                  {formData.productDescription && (
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Descrição</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{formData.productDescription}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Credit info */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">1 crédito será usado</p>
                  <p className="text-xs text-muted-foreground">Até 3 vídeos UGC em formato 9:16</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1">
                  Voltar
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 gap-2"
                  variant="hero"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Criar Minha Publi
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
